<template>
    <div
        class="main-cover"
        :class="classMainCover"
    >
        <div
            class="content-wrapper"
            :class="classContentWrapperOverlay"
        >
            <div
                class="content"
                :class="classContent"
            >
                <ContentComponentItem
                    v-for="(component, index) in data.content"
                    :key="index"
                    :data="component"
                    background="BLUE"
                />
            </div>
        </div>

        <div class="main-cover-background">
            <template
                v-if="
                    background.__typename ===
                    'ComponentMainCoverBackgroundImage'
                "
            >
                <CmsImage
                    :data="background.backgroundImage"
                    class="picture desktop"
                    is-eager
                    fit="cover"
                />

                <CmsImage
                    :data="background.mobileBackgroundImage"
                    class="picture mobile"
                    fit="cover"
                    is-eager
                />
            </template>

            <template v-else>
                <video
                    ref="videoDesktop"
                    :src="background.video.url"
                    :poster="background.cover.size_x2.url"
                    class="picture desktop"
                    autoplay
                    muted="true"
                    loop
                    @loadeddata="videoDesktop?.play()"
                ></video>

                <video
                    ref="videoMobile"
                    :src="background.video.url"
                    :poster="background.mobileCover.size_x2.url"
                    class="picture mobile"
                    autoplay
                    muted="true"
                    loop
                    @loadeddata="videoMobile?.play()"
                ></video>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, toRef, useTemplateRef } from 'vue'

import type { ComponentMainCoverFragment } from '@/graphql/api'
import { assertNever } from '@/helpers'

import ContentComponentItem from '@/components/cms/component/ContentComponentItem.vue'
import CmsImage from '@/components/cms/CmsImage.vue'

const { data } = defineProps<{
    data: ComponentMainCoverFragment
}>()

const videoDesktop = useTemplateRef('videoDesktop')
const videoMobile = useTemplateRef('videoMobile')

const background = toRef(() => data.background)

const classMainCover = computed(() => {
    const defaultHeight = ''

    switch (data.height) {
        case 'DEFAULT':
            return defaultHeight
        case 'SHORT':
            return 'short-height'
        case 'TALL':
            return 'tall-height'
        default:
            return assertNever(data.height, defaultHeight)
    }
})

const classContentWrapperOverlay = computed(() => {
    const defaultOverlay = 'overlay-bottom-to-top'

    switch (data.overlay) {
        case 'NONE':
            return ''
        case 'GRADIENT_TOP_TO_BOTTOM':
            return 'overlay-top-to-bottom'
        case 'GRADIENT_BOTTOM_TO_TOP':
            return defaultOverlay
        case 'SOLID':
            return 'overlay-solid'
        default:
            return assertNever(data.overlay, defaultOverlay)
    }
})

const classContent = computed(() => {
    const defaultWidth = ''

    switch (data.contentWidth) {
        case 'NORMAL':
            return defaultWidth
        case 'FULL':
            return 'full-width'
        default:
            return assertNever(data.contentWidth, defaultWidth)
    }
})
</script>

<style lang="scss" scoped>
.main-cover {
    width: 100%;
    position: relative;
    height: 520px;

    &.short-height {
        height: 270px;
    }

    &.tall-height {
        height: 770px;
    }

    @include min-width(1024) {
        max-height: 70vh;
        height: 700px;

        &.short-height {
            height: 490px;
        }

        &.tall-height {
            height: 910px;
        }
    }

    .main-cover-background {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;

        .picture {
            width: 100%;
            height: 100%;
            object-fit: cover;

            &.desktop {
                display: none;

                @include min-width(768) {
                    display: block;
                }
            }

            &.mobile {
                display: block;

                @include min-width(768) {
                    display: none;
                }
            }
        }
    }

    .content-wrapper {
        position: relative;
        z-index: 2;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: flex-end;
        justify-content: center;

        &.overlay-bottom-to-top {
            background: linear-gradient(
                180deg,
                rgba($black, 0),
                rgba($black, 0.5)
            );
        }

        &.overlay-top-to-bottom {
            background: linear-gradient(
                0deg,
                rgba($black, 0),
                rgba($black, 0.5)
            );
        }

        &.overlay-solid {
            background: rgba($black, 0.25);
        }

        .content {
            width: 100%;
            padding: 60px 20px;

            :deep(.text) {
                max-width: 360px;
            }

            &.full-width {
                :deep(.text) {
                    max-width: 1440px;
                }

                :deep(.th-title) {
                    max-width: 1440px;
                }
            }
        }
    }
}
</style>
